import { Suspense, lazy, useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { useRoute, aller } from '@/lib/router'
import { basculerSons, sonsActifs } from '@/lib/sons'
import { basculerPeau, peauCourante, type Peau } from '@/lib/peau'
import { RunProvider } from '@/data/RunProvider'
import Accueil from '@/views/Accueil'
import RunView from '@/views/RunView'
import Bilan from '@/views/Bilan'
import Journal from '@/views/Journal'
import Login from '@/views/Login'
import Pastille from '@/collab/Panneau'

/** Hors parcours et rarement ouvert : il ne rejoint pas le bundle principal. */
const Reglages = lazy(() => import('@/views/Reglages'))

/** La trappe : page cachée, hors navigation, chargée à la demande. On l'atteint
 *  en tapant /backdoor — le repli SPA de nginx sert index.html, et on la détecte
 *  ici sur le CHEMIN, pas sur le hash : elle n'apparaît dans aucun lien. */
const Backdoor = lazy(() => import('@/views/Backdoor'))

/** La remise : vue transverse hors parcours, chargée à la demande. */
const Remise = lazy(() => import('@/views/Remise'))
const estBackdoor = () => window.location.pathname.replace(/\/+$/, '') === '/backdoor'

export default function App() {
  const { session, pret, nom, deconnexion } = useAuth()
  const route = useRoute()
  const [sons, setSons] = useState(sonsActifs)
  const [peau, setPeau] = useState<Peau>(peauCourante)

  if (!pret) return <div className="empty" style={{ paddingTop: 60 }}>Ouverture de la session…</div>
  if (!session) return <Login />

  // La trappe court-circuite tout le reste : ni barre du haut, ni parcours, ni
  // navigation. Elle exige quand même la session (le `if` ci-dessus) — une
  // porte dérobée n'est pas une porte ouverte.
  if (estBackdoor()) {
    return (
      <Suspense fallback={<div className="empty" style={{ paddingTop: 60 }}>Ouverture de la trappe…</div>}>
        <Backdoor />
      </Suspense>
    )
  }

  const contexte = {
    // Le back-office est hors parcours : il n'a pas de journée, comme l'accueil.
    date: route.vue === 'accueil' || route.vue === 'reglages' || route.vue === 'remise' ? null : route.date,
    etape: route.vue === 'run' ? route.etape : null,
  }

  return (
    <>
      <div className="topbar">
        <div className="wrap in">
          <button className="logo" onClick={() => aller({ vue: 'accueil' })}>
            <i />FlashCP<span>CAISSE PRINCIPALE</span>
          </button>
          <span className="sp" />
          <BandeauReseau />
          <button className="tb-link" onClick={() => setPeau(basculerPeau())}
                  title="Registre : le langage de SotCP V3. Instrument : la maquette d'origine.">
            {peau === 'registre' ? 'Registre' : 'Instrument'}
          </button>
          <button className="tb-link" onClick={() => setSons(basculerSons())}
                  title="Deux sons seulement : validation d'étape et livraison Honeywell">
            {sons ? 'Sons ✓' : 'Sons ✕'}
          </button>
          <button className="tb-link" onClick={() => aller({ vue: 'reglages' })}
                  aria-current={route.vue === 'reglages' ? 'page' : undefined}>Réglages</button>
          <div className="u">OP <b>{nom.toUpperCase()}</b></div>
          <button className="tb-link" onClick={() => void deconnexion()}>Sortir</button>
        </div>
      </div>

      {route.vue === 'accueil' && <Accueil />}

      {/* Le back-office est chargé À LA DEMANDE : on l'ouvre quelques fois par
          an, il n'a rien à faire dans le bundle du parcours (§2.1). */}
      {route.vue === 'reglages' && (
        <Suspense fallback={<div className="wrap"><div className="empty">Chargement des réglages…</div></div>}>
          <Reglages />
        </Suspense>
      )}

      {route.vue === 'remise' && (
        <Suspense fallback={<div className="wrap"><div className="empty">Chargement de la remise…</div></div>}>
          <Remise date={route.date} />
        </Suspense>
      )}

      {route.vue !== 'accueil' && route.vue !== 'reglages' && route.vue !== 'remise' && (
        <RunProvider date={route.date}>
          {route.vue === 'run' && <RunView codeDemande={route.etape} />}
          {route.vue === 'bilan' && <Bilan />}
          {route.vue === 'journal' && <Journal />}
        </RunProvider>
      )}

      <Pastille contexte={contexte} />
    </>
  )
}

/** Bandeau permanent quand le réseau tombe, avec le compte des écritures en attente. */
function BandeauReseau() {
  const [enLigne, setEnLigne] = useState(() => navigator.onLine !== false)
  useEffect(() => {
    const on = () => setEnLigne(true), off = () => setEnLigne(false)
    window.addEventListener('online', on); window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [])
  if (enLigne) return null
  return <span className="tb-link" style={{ color: 'var(--warn-b)', borderColor: 'var(--warn)' }}>HORS LIGNE</span>
}


